import {
	CapMenu,
	StyledCapMenuItem,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/CapMenu';
import { isSecurityIssuedInUnits } from '@dealstack/frontend/shared/cap.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { createNumberColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/NumberColumn';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { DataSecurityTct, ProgrammeSecurityAllocationTct } from '@dealstack/shared/lib/generated';
import { Flex, Text } from '@mantine/core';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

interface Props {
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>;
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>;
	handleEditClick: (allocation: ProgrammeSecurityAllocationTct) => void;
	handleDeleteClick: (allocation: ProgrammeSecurityAllocationTct) => void;
	security: DataSecurityTct;
	hasEditorPermission?: boolean;
}

export const getSecurityAllocationColumns: (
	args: Props
) => ExtendedColumnDef<ProgrammeSecurityAllocationTct>[] = ({
	tCapManage,
	tCommonUi,
	handleEditClick,
	handleDeleteClick,
	security,
	hasEditorPermission = true,
}) => {
	const issuedInUnits = isSecurityIssuedInUnits(security.issuedIn);
	const allocationHeaderType = issuedInUnits ? tCommonUi('units') : security.currency;

	const baseColumns: ExtendedColumnDef<ProgrammeSecurityAllocationTct>[] = [
		createTextColumn({
			headerText: tCapManage('securities.details.allocations.table.columns.mipName'),
			accessorKey: 'mipId',
			enableGlobalFilter: false,
			enableSorting: false,
			enableColumnFilter: false,
			accessorFn: (row) => row.mipName,
		}),
		createNumberColumn({
			headerText: tCapManage('securities.details.allocations.table.columns.allocation', {
				type: allocationHeaderType,
			}),
			accessorKey: 'allocation',
			enableColumnFilter: false,
			enableSorting: false,
			lockPrecision: !issuedInUnits,
		}),
	];

	// Only add the actions column if user has EDITOR permission
	if (hasEditorPermission) {
		baseColumns.push({
			id: 'actions',
			cell: ({ row }) => {
				return (
					<Flex w="100%" justify="flex-end">
						<CapMenu
							width={150}
							buttonTargetIcon={<Icon variant="holding-group" size="lg" />}
							buttonVariant="subtle"
							buttonColor="gray"
						>
							<StyledCapMenuItem
								p="0.5rem"
								onClick={() => handleEditClick(row.original)}
								leftSection={<Icon variant="edit-pencil" size="md" />}
							>
								<Text size="B6Medium" truncate>
									{tCapManage(
										'securities.details.allocations.table.actions.edit_allocation'
									)}
								</Text>
							</StyledCapMenuItem>

							<StyledCapMenuItem
								p="0.5rem"
								onClick={() => handleDeleteClick(row.original)}
								leftSection={<Icon variant="trash-can" size="md" />}
								color="red.6"
							>
								<Text size="B6Medium" truncate>
									{tCapManage(
										'securities.details.allocations.table.actions.delete_allocation'
									)}
								</Text>
							</StyledCapMenuItem>
						</CapMenu>
					</Flex>
				);
			},
			header: '',
			enableGlobalFilter: false,
			enableColumnFilter: false,
			size: 0,
			maxSize: 0,
		});
	}

	return baseColumns;
};
